import React, { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Chip } from "@/components/Shared";
import { UploadSimple, DownloadSimple, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_VARIANT = { valid: "success", duplicate: "neutral", error: "danger" };

export default function ResidentsMasterImport({ open, onOpenChange, onDone }) {
    const fileRef = useRef();
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("select"); // select | preview | result
    const [report, setReport] = useState(null);
    const [commitResult, setCommitResult] = useState(null);

    function reset() {
        setFile(null); setPhase("select"); setReport(null); setCommitResult(null);
    }
    function close() {
        reset();
        onOpenChange(false);
    }

    async function downloadTemplate() {
        try {
            const res = await api.get("/residents/import-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Maintayn-Residents-Master-Template.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function preview() {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/residents/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setReport(data);
            setPhase("preview");
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    async function commit() {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/residents/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCommitResult(data);
            setPhase("result");
            toast.success(`${data.created_residents} resident(s) created · ${data.linked_existing_residents} linked · ${data.updated_relationships} updated`);
            onDone?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    const committable = report ? report.valid_rows + report.duplicate_rows : 0;

    return (
        <Dialog open={open} onOpenChange={(v) => (!v ? close() : onOpenChange(v))}>
            <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Master Onboarding — Import Residents</DialogTitle>
                </DialogHeader>

                {phase === "select" && (
                    <div className="space-y-4">
                        <div className="bg-brand-sage/50 border border-brand-line rounded-sm p-4 text-sm">
                            <p className="font-medium text-brand-ink">Excel master template (3 sheets: Residents, Metadata, Instructions)</p>
                            <p className="text-brand-inkSoft mt-1">Pre-filled with every existing flat's reference info. Add each resident's row below their flat, then upload here for a validated preview before anything is created.</p>
                            <button data-testid="residents-download-template-btn" onClick={downloadTemplate}
                                className="mt-3 inline-flex items-center gap-1 text-brand-action text-xs hover:underline">
                                <DownloadSimple size={14} /> Download template
                            </button>
                        </div>
                        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
                            data-testid="residents-master-import-file-input"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        <Button data-testid="residents-master-import-pick-file" type="button" variant="outline" onClick={() => fileRef.current?.click()}
                            className="rounded-full border-brand-line w-full">
                            <FileXls size={14} className="mr-1.5" />
                            {file ? file.name : "Choose the filled .xlsx file"}
                        </Button>
                    </div>
                )}

                {phase === "preview" && report && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                            <MiniStat label="Total rows" value={report.total_rows} />
                            <MiniStat label="Valid" value={report.valid_rows} accent="success" />
                            <MiniStat label="Duplicate" value={report.duplicate_rows} accent="neutral" />
                            <MiniStat label="Errors" value={report.error_rows} accent="danger" />
                            <MiniStat label="New residents" value={report.will_create_residents} />
                            <MiniStat label="Relationships to update" value={report.will_update_relationships} />
                        </div>
                        <div className="border border-brand-line rounded-sm max-h-80 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-brand-sage/60 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">Row</th>
                                        <th className="text-left p-2">Flat</th>
                                        <th className="text-left p-2">Resident</th>
                                        <th className="text-left p-2">Relationship</th>
                                        <th className="text-left p-2">Primary</th>
                                        <th className="text-left p-2">Status</th>
                                        <th className="text-left p-2">Result</th>
                                        <th className="text-left p-2">Issues</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.rows.map((r) => (
                                        <tr key={r.row} data-testid={`residents-import-row-${r.row}`} className="border-t border-brand-line">
                                            <td className="p-2">{r.row}</td>
                                            <td className="p-2">{r.flat || "—"}</td>
                                            <td className="p-2">{r.resident_name || "—"}</td>
                                            <td className="p-2">{r.relationship || "—"}</td>
                                            <td className="p-2">{r.primary_contact || "—"}</td>
                                            <td className="p-2">{r.status || "—"}</td>
                                            <td className="p-2">
                                                <Chip data-testid={`residents-import-row-status-${r.row}`} variant={STATUS_VARIANT[r.validation_status]}>{r.validation_status}</Chip>
                                            </td>
                                            <td className="p-2 text-brand-inkSoft">
                                                {r.errors?.length > 0 ? (
                                                    <ul className="space-y-1">
                                                        {r.errors.map((e, i) => (
                                                            <li key={i}><b>{e.field}:</b> {e.problem}</li>
                                                        ))}
                                                    </ul>
                                                ) : r.validation_status === "duplicate" ? "Already exists — will be synced, not duplicated" : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {phase === "result" && commitResult && (
                    <div className="space-y-4" data-testid="residents-master-import-result">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <MiniStat label="Residents created" value={commitResult.created_residents} accent="success" />
                            <MiniStat label="Existing linked" value={commitResult.linked_existing_residents} accent="success" />
                            <MiniStat label="Relationships updated" value={commitResult.updated_relationships} accent="neutral" />
                            <MiniStat label="Errors" value={commitResult.error_rows} accent="danger" />
                        </div>
                        {commitResult.commit_errors?.length > 0 && (
                            <div className="border border-brand-line rounded-sm p-3 text-xs bg-[#FEF2F2]/40">
                                <p className="font-medium text-brand-action mb-1">Commit errors</p>
                                <ul className="space-y-1 text-brand-inkSoft">
                                    {commitResult.commit_errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}
                        {commitResult.created_residents > 0 && (
                            <div className="text-xs text-brand-inkSoft bg-brand-sage/40 border border-brand-line rounded-sm p-2.5" data-testid="residents-master-import-password-note">
                                Newly created residents start on the default password <span className="font-mono font-medium text-brand-ink">Welcome@123</span> and will be required to set their own on first login. Please share this with them.
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {phase === "select" && (
                        <>
                            <Button type="button" variant="outline" onClick={close} className="rounded-full">Cancel</Button>
                            <Button type="button" data-testid="residents-master-import-preview-btn" disabled={!file || busy} onClick={preview}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {busy ? "Validating..." : "Preview"}
                            </Button>
                        </>
                    )}
                    {phase === "preview" && (
                        <>
                            <Button type="button" data-testid="residents-master-import-back-btn" variant="outline" onClick={() => setPhase("select")} className="rounded-full">Back</Button>
                            <Button type="button" data-testid="residents-master-import-commit-btn" disabled={busy || committable === 0} onClick={commit}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {busy ? "Importing..." : `Commit Import (${committable} row${committable === 1 ? "" : "s"})`}
                            </Button>
                        </>
                    )}
                    {phase === "result" && (
                        <Button type="button" data-testid="residents-master-import-done-btn" onClick={close} className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            Done
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MiniStat({ label, value, accent }) {
    return (
        <div className="border border-brand-line rounded-sm p-3 bg-white">
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label}</p>
            <p className={`font-heading text-xl mt-1 ${accent === "success" ? "text-[#059669]" : accent === "danger" ? "text-brand-action" : "text-brand-ink"}`}>{value ?? 0}</p>
        </div>
    );
}
